import React from 'react';
import RowerLegendBadge from '../../../ui/RowerLegendBadge';

interface RowerData {
  id: string | number;
  position?: string | number;
  power: number;
  consistency?: number;
}

interface PerformanceMetricsTableProps {
  rowerData: RowerData[];
  rowerContext?: import('../../../../types/rowerContext').RowerContext | null;
  nameMapping?: Record<string | number, string> | null;
  tableWidth: string;
  tableStyles: {
    headerPadding: string;
    cellPadding: string;
    headerFont: string;
    cellFont: string;
  };
  useHorizontalLayout: boolean;
  focusedRowerId?: string | number | null;
  onRowClick?: (rowerId: string | number) => void;
}

const PerformanceMetricsTable: React.FC<PerformanceMetricsTableProps> = ({
  rowerData,
  rowerContext,
  nameMapping,
  tableWidth,
  tableStyles,
  useHorizontalLayout,
  focusedRowerId,
  onRowClick,
}) => {
  return (
    <div
      className={`${tableWidth} ${useHorizontalLayout ? 'flex-shrink-0 mt-16' : ''} pt-1 lg:pt-2`}
    >
      <table className="ui-table w-full border border-border rounded-lg table-fixed">
        <thead className="ui-table-head">
          <tr>
            <th
              className={`ui-table-head-cell text-center ${tableStyles.headerPadding} ${tableStyles.headerFont} tracking-tight`}
            >
              Rower
            </th>
            <th
              className={`ui-table-head-cell text-center ${tableStyles.headerPadding} ${tableStyles.headerFont} tracking-tight`}
            >
              Avg
            </th>
            <th
              className={`ui-table-head-cell text-center ${tableStyles.headerPadding} ${tableStyles.headerFont} tracking-tight`}
            >
              Cons
            </th>
          </tr>
        </thead>
        <tbody className="ui-table-body">
          {rowerData.map((rower) => {
            const rowerId = rower.position || rower.id;
            const isFocusedByContext = rowerContext ? rowerId === rowerContext.position : true;
            const isFocusedByClick = focusedRowerId != null ? rowerId === focusedRowerId : true;
            const isFocused = isFocusedByContext && isFocusedByClick;
            const isSelected = focusedRowerId != null && rowerId === focusedRowerId;
            return (
              <tr
                key={rower.id}
                onClick={() => onRowClick?.(rowerId)}
                className={`ui-table-row transition-all duration-200 ${!isFocused ? 'opacity-40' : ''} ${isSelected ? 'bg-row-warning/40 ring-1 ring-inset ring-amber-400/50' : ''}`}
              >
                <td className={`${tableStyles.cellPadding} whitespace-nowrap`}>
                  <div className="flex flex-col items-center">
                    <RowerLegendBadge
                      rowerKey={`Rower ${rower.position || rower.id}`}
                      rowerNumber={rower.position || rower.id}
                    />
                    {nameMapping?.[rower.position || rower.id] && (
                      <span
                        className={`text-[0.6rem] leading-tight truncate max-w-[10rem] ${!isFocused ? 'text-content-muted' : 'text-content-secondary'}`}
                        title={nameMapping[rower.position || rower.id]}
                      >
                        {nameMapping[rower.position || rower.id]}
                      </span>
                    )}
                  </div>
                </td>
                <td
                  className={`${tableStyles.cellPadding} whitespace-nowrap text-center ui-table-cell-secondary ${tableStyles.cellFont} ${!isFocused ? 'opacity-60' : ''}`}
                >
                  {Math.round(rower.power)}W
                </td>
                <td
                  className={`${tableStyles.cellPadding} whitespace-nowrap text-center ui-table-cell-secondary ${tableStyles.cellFont} ${!isFocused ? 'opacity-60' : ''}`}
                >
                  {rower.consistency ? Math.round(rower.consistency) : '--'}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PerformanceMetricsTable;
